import { useLocation, useNavigate } from "react-router-dom";
import { ContainerCard, EmployeeDetails } from "../../components/ui/cards";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { AlertCircle, Calendar, CalendarDays, CheckCircle, ChevronLeftIcon, Circle, Clock, Download, Edit, Eye, FileText, Filter, IndianRupee, Minus, MinusCircle, PieChart, Play, PlusCircle, Send, TrendingUp, UserCheck, Users, Zap, X, Stethoscope } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { PAYROLL_PAYSLIP_EDIT, PAYROLL_PROCESS, PAYROLL_PUBLISH, PAYROLL_BANK_ADVICE, INTEGRATION_DOCTOR_SHARE_DETAIL } from "../../constants/api";
import { Reactable } from "../../components/ui/reactable";
import ToastMsg from "../../components/common/ToastMsg";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { getCSVData, checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import { cn } from "../../utils/tailwindUtils";

/**
 * Overtime cell — shows total OT hours + amount, with a click-open mini calendar
 * highlighting the days overtime was applied (hours per day on hover). The panel
 * is fixed-positioned so it escapes the table's horizontal overflow clipping.
 */
const OvertimeCell = ({ data, periodFrom }) => {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState(null);
    const btnRef = useRef(null);

    const amount = Number(data?.amount || 0);
    const totalHours = Number(data?.total_hours || 0);
    const dates = Array.isArray(data?.dates) ? data.dates : [];

    if (amount <= 0 && totalHours <= 0) {
        return <span className="text-gray-300 dark:text-gray-600">—</span>;
    }

    const toggle = () => {
        if (!open && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setCoords({
                top: r.bottom + 6,
                left: Math.max(8, Math.min(r.left, window.innerWidth - 268)),
            });
        }
        setOpen((o) => !o);
    };

    // Month to render: first OT date, else the payroll period start.
    const anchor = dates.length ? moment(dates[0].date) : (periodFrom ? moment(periodFrom) : moment());
    const daysInMonth = anchor.daysInMonth();
    const leadingBlanks = anchor.clone().startOf("month").day(); // 0=Sun
    const hoursByDay = {};
    dates.forEach((d) => {
        const m = moment(d.date);
        if (m.month() === anchor.month() && m.year() === anchor.year()) {
            hoursByDay[m.date()] = (hoursByDay[m.date()] || 0) + Number(d.hours || 0);
        }
    });
    const cells = [];
    for (let i = 0; i < leadingBlanks; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                onClick={toggle}
                title="View overtime days"
                className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
            >
                <Clock className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{totalHours}h</span>
                <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                    ₹{amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </span>
            </button>

            {open && coords && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div
                        className="fixed z-50 w-64 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl p-3"
                        style={{ top: coords.top, left: coords.left }}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                                {anchor.format("MMMM YYYY")}
                            </span>
                            <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                        <div className="grid grid-cols-7 gap-0.5 mb-1 text-[10px] text-center text-gray-400 dark:text-gray-500">
                            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                                <div key={i}>{d}</div>
                            ))}
                        </div>
                        <div className="grid grid-cols-7 gap-0.5">
                            {cells.map((d, i) =>
                                d === null ? (
                                    <div key={i} />
                                ) : (
                                    <div
                                        key={i}
                                        title={hoursByDay[d] ? `${hoursByDay[d]}h overtime` : ""}
                                        className={cn(
                                            "h-7 rounded-md text-[11px] flex items-center justify-center",
                                            hoursByDay[d]
                                                ? "bg-emerald-500 text-white font-semibold cursor-default"
                                                : "text-gray-500 dark:text-gray-400"
                                        )}
                                    >
                                        {d}
                                    </div>
                                )
                            )}
                        </div>
                        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-xs">
                            <span className="text-gray-500 dark:text-gray-400">
                                Total <span className="font-semibold text-gray-800 dark:text-gray-200">{totalHours}h</span>
                            </span>
                            <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                ₹{amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </span>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

const ADJUST_THEME = {
    emerald: { dot: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400", add: "text-emerald-600 hover:text-emerald-700 dark:text-emerald-400", focus: "focus:border-emerald-400 focus:ring-emerald-400/25" },
    rose: { dot: "bg-rose-500", text: "text-rose-600 dark:text-rose-400", add: "text-rose-600 hover:text-rose-700 dark:text-rose-400", focus: "focus:border-rose-400 focus:ring-rose-400/25" },
    indigo: { dot: "bg-indigo-500", text: "text-indigo-600 dark:text-indigo-400", add: "text-indigo-600 hover:text-indigo-700 dark:text-indigo-400", focus: "focus:border-indigo-400 focus:ring-indigo-400/25" },
};

/** Compact editable list of {name, amount} rows (Credits / Debits / Reimbursements). */
const AdjustmentGroup = ({ title, accent = "emerald", items = [], total, namePlaceholder, onAdd, onChange, onRemove }) => {
    const theme = ADJUST_THEME[accent] || ADJUST_THEME.emerald;
    const inputCls = cn(
        "min-w-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 transition",
        theme.focus
    );
    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <span className={cn("h-2 w-2 rounded-full", theme.dot)} />
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h4>
                </div>
                <button type="button" onClick={onAdd} className={cn("inline-flex items-center gap-1 text-xs font-medium transition-colors", theme.add)}>
                    <PlusCircle className="h-3.5 w-3.5" /> Add
                </button>
            </div>
            <div className="space-y-2">
                {items?.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                        <input
                            placeholder={namePlaceholder}
                            value={item.name}
                            onChange={(e) => onChange(index, "name", e.target.value)}
                            className={cn(inputCls, "flex-1")}
                        />
                        <div className="relative w-24 flex-shrink-0">
                            <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">₹</span>
                            <input
                                type="number"
                                placeholder="0"
                                value={item.amount}
                                onChange={(e) => onChange(index, "amount", e.target.value)}
                                className={cn(inputCls, "w-full pl-5 text-right")}
                            />
                        </div>
                        {items.length > 1 ? (
                            <button
                                type="button"
                                onClick={() => onRemove(index)}
                                className="flex-shrink-0 grid place-items-center h-7 w-7 rounded-md text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        ) : (
                            <span className="w-7 flex-shrink-0" />
                        )}
                    </div>
                ))}
            </div>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-gray-700/60">
                <span className="text-xs text-gray-500 dark:text-gray-400">Subtotal</span>
                <span className={cn("text-sm font-semibold", theme.text)}>₹{Number(total || 0).toLocaleString("en-IN")}</span>
            </div>
        </div>
    );
};

const RunPayrollProcess = () => {
    const navigate = useNavigate()
    const location = useLocation();
    const { fetch, create, update } = useApiMethods()
    const { fromEdit, PaySlipId, editDetails, isActive } = location.state || {};
    // Payroll mutations (edit payslip, publish, finalize) require edit permission.
    const { edit: editPermission } = checkUserPermissionFunc("payroll", "Payroll", "SubMenu");
    const [loading, setLoading] = useState(false);
    const [individualLoading, setIndividualLoading] = useState({});
    const [runPayrollLoading, setRunPayrollLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [hasCompletedAnimation, setHasCompletedAnimation] = useState(() => {
        // Check sessionStorage on initial load to persist state across refreshes
        return sessionStorage.getItem("hasCompletedAnimation") === "true";
    });
    const [payrollData, setPayrollData] = useState(null);
    const [everythingPublished, setEverythingPublished] = useState(false);
    const [RunPayrollProcessData, setRunPayrollProcessData] = useState([])

    const [openDialog, setOpenDialog] = useState(false)
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
    const [selectedRow, setSelectedRow] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    // Add these state variables
    const [debitList, setDebitList] = useState([{ name: "", amount: 0 }]);
    const [totalDebit, setTotalDebit] = useState(0);
    const [creditList, setCreditList] = useState([{ name: "", amount: 0 }]);
    const [totalCredit, setTotalCredit] = useState(0);
    const [reimburseList, setReimburseList] = useState([{ name: "", amount: 0 }]);
    const [totalReimburse, setTotalReimburse] = useState(0);

    // eMedHub doctor-share "view details" modal
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shareLoading, setShareLoading] = useState(false);
    const [shareDetail, setShareDetail] = useState(null);
    const [shareEmployee, setShareEmployee] = useState(null);

    const openShareDetail = async (row) => {
        setShareEmployee(row);
        setShareDetail(null);
        setShareModalOpen(true);
        const res = await fetch(INTEGRATION_DOCTOR_SHARE_DETAIL, {
            employee_id: row.employee,
            from_date: payrollData?.from_date,
            to_date: payrollData?.to_date,
        }, setShareLoading);
        if (res?.status === "success") {
            setShareDetail(res.data || { data: [], Total_amount_card: "0.00", Doctor_share_card: "0.00" });
        } else {
            ToastMsg("error", res?.message || "Could not load doctor share details");
            setShareModalOpen(false);
        }
    };

    // Add these functions from your old code
    const calculateTotal = (list, setter) => {
        const total = list.reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0);
        setter(total);
    };

    const handleListChange = (list, setter, index, statekey, value) => {
        let newList = [...list];
        if (statekey === "amount") {
            if (value === "" || value.length <= 7) {
                newList[index]["amount"] = +value;
            }
        } else {
            newList[index]["name"] = value.trim();
        }
        setter(newList);
    };

    const handleListAdd = (list, setter) => {
        const allPreviousLevelsFilled = list.every((item) => item.name && item.amount);
        if (allPreviousLevelsFilled) {
            const newItem = { name: "", amount: 0 };
            setter([...list, newItem]);
        } else {
            ToastMsg("error", "Please fill all previous levels before adding a new one.");
        }
    };

    const handleListRemove = (list, setter, index) => {
        const newItems = [...list];
        newItems.splice(index, 1);
        setter(newItems);
    };

    // Add this useEffect after your state declarations
    useEffect(() => {
        if (openDialog && selectedRow) {
            // Initialize credits from selectedRow.credits_breakup
            const initialCredits = selectedRow.credits_breakup && typeof selectedRow.credits_breakup === 'object'
                ? Object.entries(selectedRow.credits_breakup).map(([name, amount]) => ({
                    name: name,
                    amount: amount
                }))
                : [{ name: "", amount: 0 }];

            // If credits exist but no breakup, create a default entry
            if (initialCredits.length === 0 && selectedRow.credits) {
                initialCredits.push({
                    name: "Additional Credit",
                    amount: selectedRow.credits
                });
            }
            setCreditList(initialCredits);

            // Initialize debits from selectedRow.debit_breakup
            const initialDebits = selectedRow.debit_breakup && typeof selectedRow.debit_breakup === 'object'
                ? Object.entries(selectedRow.debit_breakup).map(([name, amount]) => ({
                    name: name,
                    amount: amount
                }))
                : [{ name: "", amount: 0 }];

            // If debit exists but no breakup, create a default entry
            if (initialDebits.length === 0 && selectedRow.debit) {
                initialDebits.push({
                    name: "Additional Debit",
                    amount: selectedRow.debit
                });
            }
            setDebitList(initialDebits);

            // Initialize reimbursements from selectedRow.reimbursements_breakup
            const initialReimburse = selectedRow.reimbursements_breakup && typeof selectedRow.reimbursements_breakup === 'object'
                ? Object.entries(selectedRow.reimbursements_breakup).map(([name, amount]) => ({
                    name: name,
                    amount: amount
                }))
                : [{ name: "", amount: 0 }];

            // If reimbursements_total exists but no breakup, create a default entry
            if (initialReimburse.length === 0 && selectedRow.reimbursements_total) {
                initialReimburse.push({
                    name: "Reimbursement",
                    amount: selectedRow.reimbursements_total
                });
            }
            setReimburseList(initialReimburse);

            console.log('Initialized lists:', {
                credits: initialCredits,
                debits: initialDebits,
                reimbursements: initialReimburse
            });
        }
    }, [openDialog, selectedRow]);

    // Add useEffect for calculations
    useEffect(() => {
        calculateTotal(debitList, setTotalDebit);
        calculateTotal(creditList, setTotalCredit);
        calculateTotal(reimburseList, setTotalReimburse);
    }, [debitList, creditList, reimburseList, calculateTotal]);

    // published count
    const publishedCount = RunPayrollProcessData.filter(row => row.is_publish === true).length

    // Get the PaySlipId from sessionStorage
    const sessionPaySlipId = sessionStorage.getItem("PaySlipId");

    const steps = [
        "Initializing payroll process...",
        "Calculating salaries...",
        "Processing deductions...",
        "Generating payslips...",
        "Finalizing payroll..."
    ];

    const fetchPayrollData = async (paySlipId) => {
        try {
            // setLoading(true);
            const response = await fetch(PAYROLL_PROCESS, { id: paySlipId });
            if (response?.status === "success") {
                setPayrollData(response.data);
                setRunPayrollProcessData(response.data.result || []);
            } else {
                console.error("Failed to fetch payroll data:", response?.message);
            }
        } catch (error) {
            console.error("Error fetching payroll data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Clear sessionStorage when component unmounts or when navigating away
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Clear sessionStorage when page is refreshed or closed
            // sessionStorage.removeItem("PaySlipId");
            sessionStorage.removeItem("hasCompletedAnimation");
        };

        const handlePopState = () => {
            // Clear sessionStorage when browser back/forward is used
            // sessionStorage.removeItem("PaySlipId");
            sessionStorage.removeItem("hasCompletedAnimation");
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('popstate', handlePopState);
        };
    }, [location, navigate]);

    useEffect(() => {
        // Handle new payroll creation with loading animation
        if (PaySlipId && !fromEdit && !hasCompletedAnimation) {
            // Store in sessionStorage BEFORE starting animation
            sessionStorage.setItem("PaySlipId", PaySlipId);
            sessionStorage.setItem("hasCompletedAnimation", "false");

            setRunPayrollLoading(true);
            setCurrentStep(0);

            const stepInterval = setInterval(() => {
                setCurrentStep(prev => {
                    if (prev >= steps.length - 1) {
                        clearInterval(stepInterval);
                        return prev;
                    }
                    return prev + 1;
                });
            }, 600);

            const timeout = setTimeout(() => {
                clearInterval(stepInterval);
                setRunPayrollLoading(false);
                setCurrentStep(0);
                setHasCompletedAnimation(true);
                sessionStorage.setItem("hasCompletedAnimation", "true");

                // Fetch data after animation completes
                fetchPayrollData(PaySlipId);

                // Clear the state after animation completes
                navigate(location.pathname, {
                    replace: true,
                    state: {
                        ...location.state,
                        PaySlipId: undefined
                    }
                });
            }, 3000);

            return () => {
                clearInterval(stepInterval);
                clearTimeout(timeout);
            };
        }

        // Handle editing existing payroll
        if (fromEdit && editDetails) {
            setLoading(true);
            // Fetch existing payroll data for editing
            if (editDetails.id) {
                fetchPayrollData(editDetails.id);
            } else {
                setTimeout(() => {
                    setLoading(false);
                }, 1500);
            }
        }

        // Handle case when coming back to page with sessionStorage data (including after refresh)
        if (sessionPaySlipId && !PaySlipId && !fromEdit) {
            // If we have a session PaySlipId but no animation state, fetch the data
            if (hasCompletedAnimation || sessionStorage.getItem("hasCompletedAnimation") === "true") {
                // Animation was completed previously, just fetch data
                fetchPayrollData(sessionPaySlipId);
            } else {
                // This is a fresh visit with session data but no animation completed
                // You might want to show a different state or directly fetch data
                fetchPayrollData(sessionPaySlipId);
                setHasCompletedAnimation(true);
                sessionStorage.setItem("hasCompletedAnimation", "true");
            }
        }

    }, [PaySlipId, fromEdit, editDetails, hasCompletedAnimation, sessionPaySlipId, navigate, location]);

    // handle edit the record 
    const handleEdit = (record) => {
        if (!editPermission) { ToastMsg("error", "You don't have permission to edit payroll"); return; }
        setSelectedRow(record);
        setEditId(record?.payslip_id);
        setEditMode(true);
        setOpenDialog(true);
    }

    // dialog open close
    const handleDialogClose = () => {
        setOpenDialog(false);
        // Reset lists after a short delay to ensure dialog is closed
        setTimeout(() => {
            setCreditList([{ name: "", amount: 0 }]);
            setDebitList([{ name: "", amount: 0 }]);
            setReimburseList([{ name: "", amount: 0 }]);
            setTotalCredit(0);
            setTotalDebit(0);
            setTotalReimburse(0);
        }, 300);
    }

    // Handle save changes
    const handleDialogSave = async () => {
        setEditLoading(true);
        try {
            // Calculate total paid salary
            const totalPaidSalary = ((selectedRow?.earnings || 0) +
                (totalCredit || 0) +
                (totalReimburse || 0) -
                (selectedRow?.deductions || 0) -
                (totalDebit || 0) -
                (selectedRow?.lop_amount || 0));

            // Convert arrays to objects for API
            const arrayToObject = (array) => {
                const obj = {};
                array.forEach(item => {
                    if (item.amount !== 0 && item.name) {
                        obj[item.name] = item.amount;
                    }
                });
                return obj;
            };

            const updatedFormData = {
                payslip_id: editId,
                debit: totalDebit || 0,
                debit_breakup: arrayToObject(debitList),
                credits: totalCredit || 0,
                credits_breakup: arrayToObject(creditList),
                lop_amount: selectedRow?.lop_amount || 0,
                lop_days: selectedRow?.lop_days || 0,
                reimbursements: arrayToObject(reimburseList),
                reimbursements_total: totalReimburse || 0,
                paid_salary: totalPaidSalary || 0
            };

            // console.log(JSON.stringify(updatedFormData));

            // Call your API here
            await update(PAYROLL_PAYSLIP_EDIT, updatedFormData);

            // reseting the credit, debit, reimb
            setCreditList([{ name: "", amount: 0 }])
            setTotalCredit(0)
            setDebitList([{ name: "", amount: 0 }])
            setTotalDebit(0)
            setReimburseList([{ name: "", amount: 0 }])
            setTotalReimburse(0)

            setOpenDialog(false);
            ToastMsg("success", "Payroll updated successfully!");
            fetchPayrollData(sessionPaySlipId || PaySlipId || editDetails?.id);

        } catch (error) {
            console.error('Error updating payroll:', error);
            ToastMsg("error", "Failed to update payroll");
        } finally {
            setEditLoading(false);
        }
    };

    // Publish individual employee payslips (mark them as published)
    const handlePublishForAll = async () => {
        if (!editPermission) { ToastMsg("error", "You don't have permission to publish payroll"); return; }
        try {
            setLoading(true);

            const nonPublishedData = RunPayrollProcessData.filter(row => row.is_publish === false);

            let updatedFormData = {
                payslip_ids: nonPublishedData.map(row => row.payslip_id),
                action: 'publish_payslips' // Add action type for clarity
            };

            // console.log("Publishing payslips:", JSON.stringify(updatedFormData));

            let response = await update(PAYROLL_PUBLISH, updatedFormData, false);
            if (response.status !== "fail") {
                setEverythingPublished(true);
                setLoading(false);
                ToastMsg("success", `Published ${nonPublishedData.length} employee payslips`);
                fetchPayrollData(sessionPaySlipId || PaySlipId || editDetails.id);
            } else {
                ToastMsg("error", response.message);
                setLoading(false);
            }
        } catch (error) {
            ToastMsg("error", error.message);
            setLoading(false);
        }
    }

    // Finalize the entire payroll run
    const handleFinalizePayroll = async () => {
        if (!editPermission) { ToastMsg("error", "You don't have permission to finalize payroll"); return; }
        try {
            setLoading(true);

            let updatedFormData = {
                payroll_id: sessionPaySlipId || PaySlipId || editDetails.id,
                published_date: moment().format("YYYY-MM-DD HH:mm:ss"),
                action: 'finalize_payroll' // Add action type for clarity
            };

            // console.log("Finalizing payroll:", JSON.stringify(updatedFormData));

            let response = await update(PAYROLL_PUBLISH, updatedFormData, false);
            if (response.status !== "fail") {
                setLoading(false);
                ToastMsg("success", "Payroll finalized successfully!");
                fetchPayrollData(sessionPaySlipId || PaySlipId || editDetails.id);
            } else {
                ToastMsg("error", response.message);
                setLoading(false);
            }
        } catch (error) {
            ToastMsg("error", error.message);
            setLoading(false);
        }
    }

    const handleIndividualPublish = async (record) => {
        if (!editPermission) { ToastMsg("error", "You don't have permission to publish payroll"); return; }
        try {
            // Set loading for specific row
            setIndividualLoading(prev => ({ ...prev, [record.payslip_id]: true }));

            let updatedFormData = {
                payslip_ids: record?.payslip_id ? [record.payslip_id] : [],
            };

            let response = await update(PAYROLL_PUBLISH, updatedFormData, false)

            if (response.status !== "fail") {
                ToastMsg("success", 'Payroll Published Successfully');
                fetchPayrollData(sessionPaySlipId || PaySlipId || editDetails.id);
            } else {
                ToastMsg("error", response.message);
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            // Clear loading for specific row
            setIndividualLoading(prev => ({ ...prev, [record.payslip_id]: false }));
        }
    }

    const handleExportToExcel = async (type) => {
        try {
            setLoading(true);

            // Add a small delay to show loading state
            await new Promise(resolve => setTimeout(resolve, 500));

            const response = await getCSVData(PAYROLL_PROCESS, { id: sessionPaySlipId || PaySlipId || editDetails.id }, type);
            if (response) {
                if (response?.status !== "fail") {
                    ToastMsg("success", "Data exported successfully!");
                } else {
                    ToastMsg("error", response?.message || "Failed to export data");
                }
            }
        } catch (error) {
            console.error("Export error:", error);
            ToastMsg("error", error.message || "Failed to export data");
        } finally {
            setLoading(false);
        }
    }

    // Download the bank / NEFT payment advice (published payslips + bank details).
    const handleBankAdvice = async () => {
        try {
            setLoading(true);
            const response = await getCSVData(PAYROLL_BANK_ADVICE, { id: sessionPaySlipId || PaySlipId || editDetails.id }, "csv");
            if (response && response?.status === "fail") {
                ToastMsg("error", response?.message || "No published payslips to export");
            } else {
                ToastMsg("success", "Bank advice downloaded");
            }
        } catch (error) {
            console.error("Bank advice error:", error);
            ToastMsg("error", error.message || "Failed to download bank advice");
        } finally {
            setLoading(false);
        }
    }

    const handleAttendanceOpen = () => {
        setConfirmDialogOpen(true)
    }

    const handleAttendanceClose = () => {
        setConfirmDialogOpen(false)
    }

    const handleAttendanceConfirm = () => {
        navigate('/attendance/attendancelog', { state: { from: 'payroll', payrollPeriod: payrollData?.payroll_month } })
    }

    const payrollColumns = [
        {
            accessor: "employee_name",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            maxWidth: "280px",
            Cell: ({ value, row }) => {
                const record = row;

                const empDetails = {
                    ...record?.employee,
                    designation_id: record?.designation_id ? {
                        id: record.designation_id,
                        designation_name: record.designation_name,
                    } : { id: 0, designation_name: "" },
                    first_name: record?.employee_name || "-",
                    id: record?.employee_code || "-",
                };

                return <EmployeeDetails details={empDetails} size="xs" />;
            },
        },
        {
            accessor: "employee_code",
            Header: "Employee ID",
            sortable: true,
            filterable: true,
            width: "140px",
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "designation_name",
            Header: "Designation",
            sortable: true,
            filterable: true,
            width: "180px",
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "ctc",
            Header: "CTC",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-medium text-gray-900 dark:text-white">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        {
            accessor: "earnings",
            Header: "Earnings",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-medium text-green-600 dark:text-green-400">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        {
            accessor: "deductions",
            Header: "Deductions",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-medium text-red-600 dark:text-red-400">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        {
            accessor: "lop_amount",
            Header: "LOP Amount",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-medium text-amber-600 dark:text-amber-400">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        {
            accessor: "net_pay",
            Header: "Net Pay",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-bold text-blue-600 dark:text-blue-400">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        // Doc Share — only when an eMedHub integration is active for this company.
        ...(payrollData?.emedhub_connected ? [{
            accessor: "doctor_share",
            Header: "Doc Share",
            sortable: true,
            width: "160px",
            Cell: ({ value, row }) => {
                const record = row.original || row;
                const amount = Number(value || 0);
                return (
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-purple-600 dark:text-purple-400">
                            ₹{amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                        {amount > 0 && (
                            <button
                                type="button"
                                title="View doctor share breakdown"
                                onClick={() => openShareDetail(record)}
                                className="p-1 rounded-md hover:bg-purple-50 dark:hover:bg-purple-900/20 text-purple-600 dark:text-purple-400"
                            >
                                <Eye className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                );
            },
        }] : []),
        {
            accessor: "paid_salary",
            Header: "Paid Salary",
            sortable: true,
            width: "130px",
            Cell: ({ value }) => (
                <span className="font-semibold text-gray-900 dark:text-white">
                    ₹{value?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                </span>
            ),
        },
        {
            accessor: "overtime",
            Header: "Overtime",
            sortable: false,
            width: "150px",
            Cell: ({ value }) => (
                <OvertimeCell data={value} periodFrom={payrollData?.from_date} />
            ),
        },
        {
            accessor: "is_publish",
            Header: "Status",
            sortable: true,
            width: "120px",
            Cell: ({ value }) => (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value
                    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                    : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                    }`}>
                    {value ? "Published" : "Draft"}
                </span>
            ),
        },
        {
            accessor: "actions",
            Header: "Actions",
            width: "120px",
            fixed: true,
            align: 'right',
            Cell: ({ row }) => {
                const record = row;
                const isRowLoading = individualLoading[record.payslip_id];

                return (
                    <div className={cn(`flex gap-2`, isRowLoading ? ' justify-end' : 'items-center')}>
                        {!record.is_publish && (
                            <>
                                {
                                    isRowLoading ? (
                                        <div className="dark:bg-gray-900 flex items-end justify-end rounded-md">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
                                        </div>
                                    ) : editPermission ? (
                                        <>
                                            <button
                                                onClick={() => handleEdit(record)}
                                                className="p-1.5 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                                title="Edit"
                                            >
                                                <Edit className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleIndividualPublish(record)}
                                                className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                                                title="Publish"
                                            >
                                                <Send className="h-4 w-4" />
                                            </button>
                                        </>
                                    ) : null
                                }
                            </>
                        )}
                    </div>
                );
            },
        },
    ];

    // Show loading for new payroll creation
    if (runPayrollLoading && PaySlipId && !fromEdit) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex flex-col items-center justify-center rounded-md">
                <div className="max-w-md w-full mx-4 p-8 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700">
                    {/* Animated Spinner */}
                    <div className="flex justify-center mb-6">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-sidebar-primary"></div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-6">
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <span>Step {currentStep + 1} of {steps.length}</span>
                            <span>{Math.round(((currentStep + 1) / steps.length) * 100)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                                className="bg-sidebar-primary h-2 rounded-full transition-all duration-500 ease-out"
                                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                            ></div>
                        </div>
                    </div>

                    {/* Current Step Text */}
                    <div className="text-center">
                        <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                            Creating New Payroll
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-1">
                            {steps[currentStep]}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">
                            This will only take a moment...
                        </p>
                    </div>

                    {/* Step Indicators */}
                    <div className="flex justify-center mt-6 space-x-1">
                        {steps.map((_, index) => (
                            <div
                                key={index}
                                className={`w-2 h-2 rounded-full transition-colors duration-300 ${index <= currentStep
                                    ? 'bg-sidebar-primary'
                                    : 'bg-gray-300 dark:bg-gray-600'
                                    }`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // Show loading for editing existing payroll or fetching data
    if (loading) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex flex-col items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-sidebar-primary mb-6"></div>
                <p className="text-md text-gray-700 dark:text-gray-300 mb-2">
                    {fromEdit ? "Loading Payroll Data" : "Fetching Payroll Details"}
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-center">
                    {fromEdit
                        ? "Please wait while we load the existing payroll data..."
                        : "Please wait while we fetch the payroll details..."
                    }
                </p>
                <div className="mt-6 w-64 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-sidebar-primary h-2 rounded-full animate-pulse"></div>
                </div>
            </div>
        );
    }

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center gap-4 mb-4">
                <ChevronLeftIcon
                    className="h-5 w-5 cursor-pointer text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                    onClick={() => {
                        sessionStorage.removeItem("PaySlipId");
                        sessionStorage.removeItem("hasCompletedAnimation");
                        navigate("/payroll/payrollprocess");
                    }}
                />
                <div className="flex-1">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {fromEdit ? "Edit Payroll Process" : "Payroll Process Details"}
                    </h1>
                    <p className="text-base text-gray-600 dark:text-gray-400 mt-2">
                        {fromEdit ? "Continue existing payroll data processing" : "Manage and process employee payroll calculations"}
                    </p>
                </div>

                {/* Payroll Month & Status */}
                <div className="flex items-center gap-6">
                    <div className="text-right">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Payroll Month</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                            {payrollData?.payroll_month ? moment().month(payrollData.payroll_month - 1).format('MMMM YYYY') : '-'}
                        </p>
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${payrollData?.status === 'pending'
                        ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 text-amber-800 dark:text-amber-300'
                        : payrollData?.status === 'completed'
                            ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300'
                            : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700 text-gray-800 dark:text-gray-300'
                        }`}>
                        <Circle className="h-2 w-2 fill-current" />
                        <span className="text-xs font-medium uppercase tracking-wide">
                            {{
                                'yet_to_publish': 'Yet to Publish',
                                'published': 'Published',
                                'processing': 'Processing'
                            }[payrollData?.status] || payrollData?.status || 'UNKNOWN'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Payroll Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* Total Paid Salary */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Total Salary</p>
                            <p className="text-md font-semibold text-gray-900 dark:text-white">
                                ₹{payrollData?.total_paid_salary?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                            <IndianRupee className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                        <TrendingUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                        <p className="text-xs text-gray-500 dark:text-gray-400">Net payable amount</p>
                    </div>
                </div>

                {/* Total Deductions */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Total Deductions</p>
                            <p className="text-md font-semibold text-gray-900 dark:text-white">
                                ₹{payrollData?.total_deductions?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-rose-100 dark:bg-rose-900/30 rounded-xl flex items-center justify-center">
                            <Minus className="h-6 w-6 text-rose-600 dark:text-rose-400" />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                        <AlertCircle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total deductions applied</p>
                    </div>
                </div>

                {/* Total Employees */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Employees</p>
                            <p className="text-md font-semibold text-gray-900 dark:text-white">
                                {payrollData?.total_employee || 0}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                            <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                        <UserCheck className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total employees processed</p>
                    </div>
                </div>

                {/* Payroll Period */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Payroll Period</p>
                            <p className="text-md font-semibold text-gray-900 dark:text-white">
                                {payrollData?.from_date ? moment(payrollData.from_date).format('DD MMM') : '-'} - {payrollData?.to_date ? moment(payrollData.to_date).format('DD MMM') : '-'}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-violet-100 dark:bg-violet-900/30 rounded-xl flex items-center justify-center">
                            <Calendar className="h-6 w-6 text-violet-600 dark:text-violet-400" />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                        <Clock className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                        <p className="text-xs text-gray-500 dark:text-gray-400">{payrollData?.total_days || 0} working days</p>
                    </div>
                </div>
            </div>

            {/* Additional Details Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">

                {/* Financial Summary */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <PieChart className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                        <h3 className="text-md font-semibold text-gray-900 dark:text-white">Financial Summary</h3>
                    </div>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center pb-3 border-b border-gray-100 dark:border-gray-700">
                            <div className="flex items-center gap-2">
                                <TrendingUp className="h-4 w-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Gross Salary</span>
                            </div>
                            <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                                ₹{(payrollData?.total_paid_salary + payrollData?.total_deductions)?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                            </span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-gray-100 dark:border-gray-700">
                            <div className="flex items-center gap-2">
                                <Minus className="h-4 w-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Deductions</span>
                            </div>
                            <span className="text-sm font-semibold text-rose-600 dark:text-rose-400">
                                ₹{payrollData?.total_deductions?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                            </span>
                        </div>
                        <div className="flex justify-between items-center pt-2">
                            <div className="flex items-center gap-2">
                                <IndianRupee className="h-4 w-4 text-gray-400" />
                                <span className="text-md font-semibold text-gray-900 dark:text-white">Net Payable</span>
                            </div>
                            <span className="text-md font-semibold text-gray-900 dark:text-white">
                                ₹{payrollData?.total_paid_salary?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <Zap className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                        <h3 className="text-md font-semibold text-gray-900 dark:text-white">Quick Actions</h3>
                    </div>
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {/* Show completed message if payroll is completed */}
                            {payrollData?.status === 'completed' ? (
                                <div className="col-span-full">
                                    <div className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white py-3 px-4 rounded-lg text-sm font-semibold">
                                        <CheckCircle className="h-4 w-4" />
                                        Payroll Completed ✓
                                    </div>
                                    <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-2">
                                        This payroll has been finalized and cannot be modified
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {/* Publish Individual Employee Payslips */}
                                    {editPermission && !everythingPublished && publishedCount < RunPayrollProcessData.length && (
                                        <button
                                            className="w-full flex items-center justify-center gap-2 bg-sidebar dark:bg-primary hover:bg-sidebar-primary text-white py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                            onClick={handlePublishForAll}
                                            disabled={payrollData?.status === 'completed'}
                                        >
                                            <Send className="h-4 w-4" />
                                            Publish All Drafts ({RunPayrollProcessData.length - publishedCount})
                                        </button>
                                    )}

                                    {/* Finalize Entire Payroll - Full width when publish button is hidden */}
                                    {editPermission && (
                                        <button
                                            className={`w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${(!everythingPublished && publishedCount < RunPayrollProcessData.length)
                                                ? 'sm:col-span-1'
                                                : 'sm:col-span-2'
                                                }`}
                                            onClick={handleFinalizePayroll}
                                            disabled={payrollData?.status === 'completed' || publishedCount === 0}
                                        >
                                            <CheckCircle className="h-4 w-4" />
                                            Finalize Payroll
                                        </button>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Secondary Actions */}
                        <button className="w-full flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-200 hover:shadow-lg"
                            onClick={() => handleExportToExcel("csv")}
                        >
                            <Download className="h-4 w-4" />
                            Export to Excel
                        </button>
                        {editPermission && (
                            <button className="w-full flex items-center justify-center gap-2 bg-sidebar-primary hover:bg-sidebar-primary text-white py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-200 hover:shadow-lg"
                                onClick={handleBankAdvice}
                                title="Download the bank / NEFT payment file for published payslips"
                            >
                                <FileText className="h-4 w-4" />
                                Bank Advice
                            </button>
                        )}
                        <button className="w-full flex items-center justify-center gap-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-200"
                            onClick={() => handleAttendanceOpen()}
                        >
                            <Calendar className="h-4 w-4" />
                            View Attendance
                        </button>
                    </div>
                </div>
            </div>

            {/* Employee List Table Section */}
            <div className=" dark:bg-gray-800 rounded-xl dark:border-gray-700">
                <div className="py-6 px-4 border-b rounded-xl border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Left Section - Title and Description */}
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                                <Users className="h-6 w-6 text-sidebar dark:text-blue-400" />
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Employee Payroll List
                                </h3>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Manage and review individual employee salary calculations, deductions, and net pay
                            </p>
                        </div>

                        {/* Right Section - Status Summary */}
                        <div className="flex flex-col sm:flex-row gap-4 lg:gap-6">
                            {/* Total Employees Card */}
                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 px-4 py-3 min-w-[140px]">
                                <div className="flex items-center gap-2 mb-1">
                                    <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                                        Total
                                    </span>
                                </div>
                                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                                    {RunPayrollProcessData.length || 0}
                                </p>
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                    Employees
                                </p>
                            </div>

                            {/* Published Employees Card */}
                            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 px-4 py-3 min-w-[140px]">
                                <div className="flex items-center gap-2 mb-1">
                                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                                    <span className="text-sm font-medium text-green-700 dark:text-green-300 uppercase tracking-wide">
                                        Published
                                    </span>
                                </div>
                                <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                                    {publishedCount || 0}
                                </p>
                                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                    {RunPayrollProcessData.length > 0 ? Math.round((publishedCount / RunPayrollProcessData.length) * 100) : 0}% Complete
                                </p>
                            </div>

                            {/* Draft Employees Card */}
                            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800 px-4 py-3 min-w-[140px]">
                                <div className="flex items-center gap-2 mb-1">
                                    <FileText className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                                    <span className="text-sm font-medium text-amber-700 dark:text-amber-300 uppercase tracking-wide">
                                        Draft
                                    </span>
                                </div>
                                <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">
                                    {RunPayrollProcessData.length - publishedCount || 0}
                                </p>
                                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                    Pending Review
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    {RunPayrollProcessData.length > 0 && (
                        <div className="mt-6">
                            <div className="flex justify-between text-sm mb-2">
                                <span className="text-gray-600 dark:text-gray-400 font-medium">
                                    Publication Progress
                                </span>
                                <span className="text-gray-600 dark:text-gray-400 font-medium">
                                    {publishedCount} of {RunPayrollProcessData.length} completed
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                                <div
                                    className="bg-green-500 h-2.5 rounded-full transition-all duration-500 ease-out"
                                    style={{
                                        width: `${RunPayrollProcessData.length > 0 ? (publishedCount / RunPayrollProcessData.length) * 100 : 0}%`
                                    }}
                                ></div>
                            </div>
                        </div>
                    )}
                </div>
                {/* Your employee table component will go here */}
                <div className="pt-2">
                    <Reactable
                        columns={payrollColumns}
                        data={RunPayrollProcessData}
                        setData={() => { }}
                        url={PAYROLL_PROCESS}
                        filter={{ id: sessionPaySlipId || editDetails?.id }}
                        onRowHighlightKey="is_publish"
                        serverSide={true}
                        tableHeight="calc(100vh - 320px)"
                        rowSize="sm"
                        isDraftRequired={false}
                        isCSVRequired={false}
                        isPDFRequired={false}
                        isFilterRequired={false}
                    />
                </div>

                <DialogModal
                    open={openDialog}
                    onClose={handleDialogClose}
                    loading={editLoading}
                    onSave={handleDialogSave}
                    title="Edit Employee Payroll"
                    width="max-w-5xl"
                >
                    <div className="space-y-4 max-h-[calc(100vh-210px)] overflow-y-auto pr-1">
                        {/* Header */}
                        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-4 py-3">
                            <div className="min-w-0">
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{selectedRow?.employee_name}</h2>
                                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                                    <span className="inline-flex items-center rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-gray-600 dark:text-gray-300">
                                        {selectedRow?.employee_code}
                                    </span>
                                    <span className="inline-flex items-center rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-gray-600 dark:text-gray-300">
                                        {selectedRow?.designation_name}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-stretch gap-2.5">
                                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-right">
                                    <div className="text-[11px] uppercase tracking-wide text-gray-400">Current Net Pay</div>
                                    <div className="text-base font-semibold text-gray-700 dark:text-gray-200">
                                        ₹{Number(selectedRow?.net_pay || 0).toLocaleString('en-IN')}
                                    </div>
                                </div>
                                <div className="rounded-lg border border-sidebar/30 bg-sidebar/10 px-3 py-1.5 text-right">
                                    <div className="text-[11px] uppercase tracking-wide text-sidebar-primary/80">Updated Final Pay</div>
                                    <div className="text-base font-bold text-sidebar-primary">
                                        ₹{((selectedRow?.earnings || 0) + (totalCredit || 0) + (totalReimburse || 0) - (selectedRow?.deductions || 0) - (totalDebit || 0) - (selectedRow?.lop_amount || 0)).toLocaleString('en-IN')}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Main Grid Layout */}
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                            {/* Left Column - Fixed Earnings & Deductions */}
                            <div className="xl:col-span-1 space-y-4">
                                {/* Earnings */}
                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
                                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Earnings</span>
                                        <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                                            ₹{Number(selectedRow?.earnings || 0).toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                    <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
                                        {selectedRow?.earnings_breakup && Object.entries(selectedRow.earnings_breakup).map(([key, earning]) => (
                                            earning?.name && earning.monthly_amount !== null && (
                                                <div key={key} className="flex items-center justify-between px-4 py-2">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{earning.name}</span>
                                                        {earning.percentage ? (
                                                            <span className="flex-shrink-0 text-[11px] text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded">{earning.percentage}%</span>
                                                        ) : null}
                                                    </div>
                                                    <span className="flex-shrink-0 text-sm font-medium text-gray-900 dark:text-gray-100">
                                                        ₹{Number(earning.monthly_amount || 0).toLocaleString('en-IN')}
                                                    </span>
                                                </div>
                                            )
                                        ))}
                                    </div>
                                </div>

                                {/* Deductions */}
                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
                                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Deductions</span>
                                        <span className="text-sm font-semibold text-rose-600 dark:text-rose-400">
                                            ₹{Number(selectedRow?.deductions || 0).toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                    {selectedRow?.deductions_breakup && Object.keys(selectedRow.deductions_breakup).length > 0 ? (
                                        <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
                                            {Object.entries(selectedRow.deductions_breakup).map(([key, deduction]) => (
                                                <div key={key} className="flex items-center justify-between px-4 py-2">
                                                    <div className="min-w-0">
                                                        <div className="text-sm text-gray-700 dark:text-gray-300 truncate">{deduction.name}</div>
                                                        <div className="text-[11px] text-gray-400">Employer ₹{Number(deduction.employer_share_monthly_amount || 0).toLocaleString('en-IN')}</div>
                                                    </div>
                                                    <span className="flex-shrink-0 text-sm font-medium text-rose-600 dark:text-rose-400">
                                                        ₹{Number(deduction.employee_share_monthly_amount || 0).toLocaleString('en-IN')}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="px-4 py-3 text-sm text-gray-400">No deductions</div>
                                    )}
                                </div>

                                {/* Loss of Pay */}
                                <div className="rounded-xl border border-amber-200/70 dark:border-amber-800/60 bg-amber-50/50 dark:bg-amber-900/10 px-4 py-3">
                                    <div className="flex items-center justify-between gap-4">
                                        <span className="text-sm font-semibold text-amber-700 dark:text-amber-300">Loss of Pay</span>
                                        <div className="flex items-center gap-5">
                                            <div className="text-right">
                                                <div className="text-sm font-semibold text-amber-700 dark:text-amber-300">{selectedRow?.lop_days || 0}</div>
                                                <div className="text-[11px] text-amber-600/80 dark:text-amber-400/70">Days</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm font-semibold text-amber-700 dark:text-amber-300">₹{Number(selectedRow?.lop_amount || 0).toLocaleString('en-IN')}</div>
                                                <div className="text-[11px] text-amber-600/80 dark:text-amber-400/70">Amount</div>
                                            </div>
                                        </div>
                                    </div>
                                    {Number(selectedRow?.short_duty_days || 0) > 0 && (
                                        <div className="mt-2 pt-2 border-t border-amber-200/60 dark:border-amber-800/50 flex items-center justify-between text-[11px] text-amber-700/90 dark:text-amber-300/80">
                                            <span>of which short-duty deduction (missing punch)</span>
                                            <span className="font-semibold">
                                                {Number(selectedRow.short_duty_days)} {Number(selectedRow.short_duty_days) === 1 ? "day" : "days"} · ₹{Number(selectedRow?.short_duty_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    )}
                                    {Number(selectedRow?.short_duty_fine || 0) > 0 && (
                                        <div className="mt-2 pt-2 border-t border-amber-200/60 dark:border-amber-800/50 flex items-center justify-between text-[11px] text-rose-600/90 dark:text-rose-400/80">
                                            <span>short-duty fine (reference — add to Debits to charge)</span>
                                            <span className="font-semibold">
                                                {Number(selectedRow?.short_duty_fine_days || 0)} {Number(selectedRow?.short_duty_fine_days || 0) === 1 ? "day" : "days"} · ₹{Number(selectedRow?.short_duty_fine || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right Column - Focus on Adjustments */}
                            <div className="xl:col-span-2 space-y-4">
                                {/* Adjustments */}
                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Adjustments</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-4">
                                        <AdjustmentGroup
                                            title="Credits"
                                            accent="emerald"
                                            items={creditList}
                                            total={totalCredit}
                                            namePlaceholder="Credit name"
                                            onAdd={() => handleListAdd(creditList, setCreditList)}
                                            onChange={(i, f, v) => handleListChange(creditList, setCreditList, i, f, v)}
                                            onRemove={(i) => handleListRemove(creditList, setCreditList, i)}
                                        />
                                        <AdjustmentGroup
                                            title="Debits"
                                            accent="rose"
                                            items={debitList}
                                            total={totalDebit}
                                            namePlaceholder="Debit name"
                                            onAdd={() => handleListAdd(debitList, setDebitList)}
                                            onChange={(i, f, v) => handleListChange(debitList, setDebitList, i, f, v)}
                                            onRemove={(i) => handleListRemove(debitList, setDebitList, i)}
                                        />
                                        <AdjustmentGroup
                                            title="Reimbursements"
                                            accent="indigo"
                                            items={reimburseList}
                                            total={totalReimburse}
                                            namePlaceholder="Reimbursement name"
                                            onAdd={() => handleListAdd(reimburseList, setReimburseList)}
                                            onChange={(i, f, v) => handleListChange(reimburseList, setReimburseList, i, f, v)}
                                            onRemove={(i) => handleListRemove(reimburseList, setReimburseList, i)}
                                        />
                                    </div>
                                </div>

                                {/* Salary Summary strip */}
                                <div className="rounded-xl border border-sidebar/30 bg-sidebar/5 dark:bg-sidebar/10 px-4 py-3">
                                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
                                        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
                                            {[
                                                { label: "Earnings", value: selectedRow?.earnings, op: "", color: "text-gray-800 dark:text-gray-200" },
                                                { label: "Credits", value: totalCredit, op: "+", color: "text-emerald-600 dark:text-emerald-400" },
                                                { label: "Reimburse", value: totalReimburse, op: "+", color: "text-indigo-600 dark:text-indigo-400" },
                                                { label: "Deductions", value: selectedRow?.deductions, op: "−", color: "text-rose-600 dark:text-rose-400" },
                                                { label: "Debits", value: totalDebit, op: "−", color: "text-orange-600 dark:text-orange-400" },
                                                { label: "LOP", value: selectedRow?.lop_amount, op: "−", color: "text-amber-600 dark:text-amber-400" },
                                            ].map((t, i) => (
                                                <div key={t.label} className="flex items-center gap-2.5">
                                                    {i > 0 && <span className="text-gray-400 text-sm font-medium">{t.op}</span>}
                                                    <div className="text-center">
                                                        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{t.label}</div>
                                                        <div className={cn("text-sm font-semibold", t.color)}>₹{Number(t.value || 0).toLocaleString('en-IN')}</div>
                                                    </div>
                                                </div>
                                            ))}
                                            <span className="text-gray-400 text-base font-medium">=</span>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Final Net Payable</div>
                                            <div className="text-2xl font-bold text-sidebar-primary">
                                                ₹{((selectedRow?.earnings || 0) + (totalCredit || 0) + (totalReimburse || 0) - (selectedRow?.deductions || 0) - (totalDebit || 0) - (selectedRow?.lop_amount || 0)).toLocaleString('en-IN')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogModal>

                <ConfirmPopupModal
                    open={confirmDialogOpen}
                    onClose={handleAttendanceClose}
                    onConfirm={handleAttendanceConfirm}
                    title="Navigate to Attendance"
                    description="You are about to navigate and modify attendance for the selected month. Please note that any changes will require re-running the payroll calculation."
                    type="warning"
                    confirmlabel="Continue"
                    cancellabel="Cancel"
                    destructive={false}
                >
                </ConfirmPopupModal>
            </div>

            {/* eMedHub doctor-share breakdown modal */}
            {shareModalOpen && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
                    onClick={() => setShareModalOpen(false)}
                >
                    <div
                        className="w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100 dark:border-gray-700">
                            <div className="flex items-start gap-3 min-w-0">
                                <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-600 grid place-items-center">
                                    <Stethoscope size={20} />
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">eMedHub Doctor Share</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {shareEmployee?.employee_name || "Employee"}
                                        {payrollData?.from_date ? ` · ${payrollData.from_date} to ${payrollData.to_date}` : ""}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShareModalOpen(false)}
                                className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Cards */}
                        {!shareLoading && shareDetail && (
                            <div className="grid grid-cols-2 gap-3 p-5 pb-0">
                                <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3">
                                    <div className="text-xs text-gray-500 dark:text-gray-400">Total Amount</div>
                                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        ₹{Number(shareDetail.Total_amount_card || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </div>
                                </div>
                                <div className="rounded-xl border border-purple-100 dark:border-purple-900/40 bg-purple-50 dark:bg-purple-900/20 px-4 py-3">
                                    <div className="text-xs text-purple-700 dark:text-purple-300">Total Doctor Share</div>
                                    <div className="text-lg font-semibold text-purple-700 dark:text-purple-300">
                                        ₹{Number(shareDetail.Doctor_share_card || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Body */}
                        <div className="p-5 overflow-auto">
                            {shareLoading ? (
                                <div className="py-12 flex justify-center">
                                    <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-purple-500" />
                                </div>
                            ) : !shareDetail || !(shareDetail.data || []).length ? (
                                <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                    No sharable services found for this period.
                                </p>
                            ) : (
                                <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-700">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50/70 dark:bg-gray-900/30 border-b border-gray-100 dark:border-gray-700">
                                                <th className="py-2 px-3">Module</th>
                                                <th className="py-2 px-3">Service</th>
                                                <th className="py-2 px-3">Service Head</th>
                                                <th className="py-2 px-3 text-right">Count</th>
                                                <th className="py-2 px-3 text-right">Total Amount</th>
                                                <th className="py-2 px-3 text-right">Doctor Share</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(shareDetail.data || []).map((r, i) => (
                                                <tr key={i} className="border-b border-gray-50 dark:border-gray-800">
                                                    <td className="py-2 px-3">
                                                        <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                                            {r.module_type || "—"}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 px-3 text-gray-900 dark:text-gray-100">{r.service_name || "—"}</td>
                                                    <td className="py-2 px-3 text-gray-500 dark:text-gray-400">{r.service_head || "—"}</td>
                                                    <td className="py-2 px-3 text-right text-gray-500 dark:text-gray-400">{r.count}</td>
                                                    <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-200">
                                                        ₹{Number(r.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                    </td>
                                                    <td className="py-2 px-3 text-right font-medium text-purple-600 dark:text-purple-400">
                                                        ₹{Number(r.doctor_share || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </ContainerCard>
    );
};

export default RunPayrollProcess;